import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Car, CalendarCheck, CalendarDays, Wallet, MessageSquare, Star, Settings, KeyRound, Wrench, Share2, Home } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useHostData } from "@/hooks/useHostData";
import { Avatar } from "@/components/Avatar";
import { ModeSwitcher } from "@/components/ModeSwitcher";
import { HostOverview } from "@/components/host/HostOverview";
import { HostListings } from "@/components/host/HostListings";
import { HostTrips } from "@/components/host/HostTrips";
import { HostBlocks } from "@/components/host/HostBlocks";
import { FleetOverviewCalendar } from "@/components/admin/FleetOverviewCalendar";
import { HostPayouts } from "@/components/host/HostPayouts";
import { HostMessages } from "@/components/host/HostMessages";
import { HostReviews } from "@/components/host/HostReviews";
import { HostSettings } from "@/components/host/HostSettings";
import { MaintenanceManager } from "@/components/maintenance/MaintenanceManager";
import { Seo } from "@/components/Seo";

const NAV = [
  ["overview", LayoutDashboard],
  ["listings", Car],
  ["trips", CalendarCheck],
  ["calendar", CalendarDays],
  ["maintenance", Wrench],
  ["payouts", Wallet],
  ["messages", MessageSquare],
  ["reviews", Star],
  ["settings", Settings],
];

export default function HostPortal() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const h = useHostData();
  const [tab, setTab] = useState("overview");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [calView, setCalView] = useState("overview");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect")) {
      setTab("payouts");
      h.loadConnect?.();
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (h.denied) {
    return (
      <div data-testid="host-denied" className="bg-slate-50 min-h-screen pt-28 pb-24 px-5 grid place-items-center">
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 p-10 text-center max-w-md">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-blue-50 text-blue-600 mx-auto"><KeyRound size={26} /></span>
          <h2 className="font-heading text-xl font-bold text-slate-900 mt-5">{t("host.becomeHost")}</h2>
          <p className="text-slate-500 mt-2 text-sm">{t("host.becomeHostSub")}</p>
          <Link to="/host" data-testid="host-onboard-link" className="inline-block mt-6 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors">{t("host.listYourCar")}</Link>
        </div>
      </div>
    );
  }

  const openMessages = (trip) => { setSelectedTrip(trip.id); setTab("messages"); };
  const shareProfile = async () => {
    const url = `${window.location.origin}/u/${user?.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("host.profileCopied"));
    } catch {
      toast.message(url);
    }
  };
  const badge = (k) => (k === "listings" ? (h.summary?.listings?.under_review || 0) : 0);

  return (
    <div data-testid="host-portal" className="bg-slate-50 min-h-screen pt-20">
      <Seo noindex title="Host Dashboard | Belo Rentals" path="/host/dashboard" />
      <div className="flex max-w-7xl mx-auto">
        <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-5rem)] sticky top-20 self-start py-6 px-3">
          <div className="px-2 pb-4 mb-3 border-b border-slate-100">
            <ModeSwitcher className="w-full justify-between" />
            <Link to="/" data-testid="host-view-site" className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-600 transition-colors py-1.5">
              <Home size={13} /> View public site
            </Link>
          </div>
          <div className="flex items-center gap-3 px-3 pb-5 mb-3 border-b border-slate-100">
            <Avatar name={user?.name} size="md" accent />
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 text-sm truncate">{user?.name}</p>
              <p className="text-xs text-blue-600 font-medium">{t("host.hosting")}</p>
            </div>
          </div>
          <div className="space-y-1 flex-1">
            {NAV.map(([k, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`hnav-${k}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${tab === k ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                <Icon size={18} /> <span className="flex-1 text-left">{t(`host.${k}`)}</span>
                {badge(k) > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{badge(k)}</span>}
              </button>
            ))}
          </div>
        </aside>

        <main className="flex-1 min-w-0 px-5 sm:px-8 py-8">
          <div className="md:hidden mb-4"><ModeSwitcher size="lg" /></div>
          <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-3 -mx-1 px-1">
            {NAV.map(([k, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`hmnav-${k}`}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-blue-600 text-white" : "bg-white text-slate-600 shadow-card"}`}>
                <Icon size={15} /> {t(`host.${k}`)}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4 mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900" data-testid="host-section-title">{t(`host.${tab}`)}</h1>
            <button onClick={shareProfile} data-testid="host-share-profile" className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors">
              <Share2 size={16} /> <span className="hidden sm:inline">{t("host.shareProfile")}</span>
            </button>
          </div>

          {tab === "overview" && <HostOverview summary={h.summary} loading={h.loading} />}
          {tab === "listings" && <HostListings listings={h.listings} editListing={h.editListing} setListingAvailability={h.setListingAvailability} deleteListing={h.deleteListing} />}
          {tab === "trips" && <HostTrips trips={h.trips} onMessage={openMessages} onRefresh={h.load} />}
          {tab === "calendar" && (
            <div className="space-y-4">
              <div className="inline-flex items-center bg-slate-100 rounded-full p-1">
                <button onClick={() => setCalView("overview")} data-testid="hcalview-overview" className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${calView === "overview" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Fleet overview</button>
                <button onClick={() => setCalView("blocks")} data-testid="hcalview-blocks" className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${calView === "blocks" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Blocks</button>
              </div>
              {calView === "overview"
                ? <FleetOverviewCalendar apiBase="/host/me" canManage={false} />
                : <HostBlocks trips={h.trips} listings={h.listings} addBlock={h.addBlock} removeBlock={h.removeBlock} />}
            </div>
          )}
          {tab === "maintenance" && <MaintenanceManager apiBase="/host/me" cars={h.listings} />}
          {tab === "payouts" && <HostPayouts earnings={h.earnings} connect={h.connect} connectBank={h.connectBank} openBankDashboard={h.openBankDashboard} />}
          {tab === "messages" && <HostMessages trips={h.trips} selected={selectedTrip} setSelected={setSelectedTrip} />}
          {tab === "reviews" && <HostReviews />}
          {tab === "settings" && <HostSettings />}
        </main>
      </div>
    </div>
  );
}
